const longDate = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" });
const shortDate = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
const dateTime = new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" });
const relative = new Intl.RelativeTimeFormat("fr-FR", { numeric: "auto" });

function toDate(value: string): Date {
  // Plain ISO dates are calendar days: parse them as local dates, not UTC midnight.
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
}

export function formatDate(value: string | null | undefined, style: "long" | "short" = "long"): string {
  if (!value) return "date inconnue";
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return value;
  return (style === "long" ? longDate : shortDate).format(date);
}

/** Day and time, for timestamps: "23/09/2026 08:15". */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "date inconnue";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTime.format(date);
}

export function formatRelative(value: string | null | undefined, now = new Date()): string {
  if (!value) return "jamais";
  const seconds = (toDate(value).getTime() - now.getTime()) / 1000;
  const abs = Math.abs(seconds);
  if (abs < 60) return "à l'instant";
  if (abs < 3600) return relative.format(Math.round(seconds / 60), "minute");
  if (abs < 86400) return relative.format(Math.round(seconds / 3600), "hour");
  if (abs < 86400 * 30) return relative.format(Math.round(seconds / 86400), "day");
  return `le ${formatDate(value)}`;
}

export function isIsoDate(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function plural(count: number, one: string, many: string): string {
  return `${count} ${count > 1 ? many : one}`;
}

const NBSP = " ";
const compactNumber = new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 });
const signedPercent = new Intl.NumberFormat("fr-FR", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: "exceptZero",
});

/** Use a typographic minus and one kind of non-breaking space, whatever the ICU version. */
function tidy(text: string): string {
  return text.replace(/-/g, "−").replace(/\s/g, NBSP);
}

/** Compact euro amount: 1 200 000 → "1,2 M€", 950 → "950 €". */
export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const text = tidy(compactNumber.format(value));
  return /\d$/.test(text) ? `${text}${NBSP}€` : `${text}€`;
}

/** Signed percentage from a fraction: -0.124 → "−12,4 %". */
export function formatPercent(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return "—";
  return tidy(signedPercent.format(fraction));
}

/** Share price with its currency when known: 12.5, "EUR" → "12,50 €". */
export function formatPrice(value: number, currency: string | null | undefined): string {
  let format: Intl.NumberFormat;
  try {
    format = currency
      ? new Intl.NumberFormat("fr-FR", { style: "currency", currency })
      : new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch {
    // Unknown currency code: show the number and the raw code.
    return tidy(`${new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ${currency}`);
  }
  return tidy(format.format(value));
}
